<script>
    import { onDestroy, onMount } from 'svelte';
    import { config } from './stores.js';
    import { setPresetValue, setItemValue, makeElementsClickable, saveState } from './util';
    import Button from './Button.svelte';
    import Checkable from './Checkable.svelte';
    import { setData, trackEvent, dispatchEvent } from './tracking';

    let view = 'basic';
    let container;
    let mutationObserver;

    const initAdvancedTriggers = () => {
        makeElementsClickable({
            selector: '[data-open-privacy-advanced-settings="true"]',
            containerElement: container,
            callback: (e) => {
                e.preventDefault();
                changeView('advanced');
                trackEvent({
                    category: 'e_consent__open',
                    action: 'advanced',
                    eventIsNonInteraction: false,
                });
            },
        });
    };

    const handleSave = () => {
        saveState($config);
        $config.display = false;
        setData({
            event: 'e_consent__update',
        });
        dispatchEvent('Accept');
    };

    const handleAccept = () => {
        // enable all presets
        $config.presets.forEach((preset) => {
            $config = setPresetValue({
                currentValue: $config,
                presetId: preset.id,
                value: true,
            });
        });

        saveState($config);
        $config.display = false;
        setData({
            event: 'e_consent__update',
        });
        dispatchEvent('Accept');
    };

    const changeView = (viewName) => {
        view = viewName;

        trackEvent({
            category: 'e_consent__view_change',
            action: viewName,
            eventIsNonInteraction: false,
        });
    };

    onMount(async () => {
        mutationObserver = new MutationObserver(() => {
            initAdvancedTriggers();
        });

        mutationObserver.observe(container, {
            attributes: true,
            childList: true,
            subtree: true,
        });

        initAdvancedTriggers();

        trackEvent({
            category: 'e_consent__open',
            action: view,
            eventIsNonInteraction: true,
        });
    });

    onDestroy(async () => {
        mutationObserver.disconnect();
    });
</script>

{@html `<!--googleoff: all-->`}
<div class="e_consent__overlay e_consent--theme-{$config.theme}" />
<section class="e_consent e_consent--theme-{$config.theme} e_consent--view-{view}" bind:this={container}>
    <header>
        <h2>{$config.strings.headline}</h2>
        <Button type="decline" on:click={handleSave}>
            {$config.strings.decline}
        </Button>
    </header>
    <main>
        {#if view === 'basic'}
            {#if $config.strings.description}
                <p>{@html $config.strings.description}</p>
            {/if}
        {:else if view === 'advanced'}
            {#if $config.strings.description_advanced}
                <p>{@html $config.strings.description_advanced}</p>
            {/if}
            {#each $config.presets as preset, i (preset.id)}
                {#if preset.items.length}
                    <Checkable
                        id={preset.id}
                        name={preset.name}
                        description={preset.description}
                        checked={preset.value}
                        disabled={i === 0}
                        onChange={(value) => {
                            $config = setPresetValue({
                                currentValue: $config,
                                presetId: preset.id,
                                value: value,
                            });
                        }}
                    />
                {/if}
            {/each}
        {:else if view === 'detailed'}
            {#if $config.strings.description_detailed}
                <p>{@html $config.strings.description_detailed}</p>
            {/if}
            {#each $config.presets as preset, i (preset.id)}
                {#if preset.items.length}
                    <Checkable
                        id={preset.id}
                        name={preset.name}
                        description={preset.description}
                        checked={preset.value}
                        disabled={i === 0}
                        onChange={(value) => {
                            $config = setPresetValue({
                                currentValue: $config,
                                presetId: preset.id,
                                value: value,
                            });
                        }}
                    >
                        <details>
                            <summary>
                                {$config.strings.individualize}
                            </summary>
                            {#each preset.items as item (item.id)}
                                {#if item.enabled}
                                    <Checkable
                                        id={item.id}
                                        name={item.name}
                                        description={item.description}
                                        checked={item.value}
                                        disabled={i === 0}
                                        onChange={(value) => {
                                            $config = setItemValue({
                                                currentValue: $config,
                                                presetId: preset.id,
                                                itemId: item.id,
                                                value: value,
                                            });
                                        }}
                                    />
                                {/if}
                            {/each}
                        </details>
                    </Checkable>
                {/if}
            {/each}
        {/if}
    </main>
    <footer>
        {#if view !== 'basic'}
            <Button type="save" on:click={handleSave}>
                {$config.strings.save}
            </Button>
        {/if}
        <Button type="accept" on:click={handleAccept}>
            {$config.strings.accept}
        </Button>
        {#if view === 'advanced'}
            <Button
                type="link"
                on:click={() => {
                    changeView('detailed');
                }}
            >
                {$config.strings.detailed}
            </Button>
        {:else}
            <Button
                type="link"
                on:click={() => {
                    changeView('advanced');
                }}
            >
                {$config.strings.advanced}
            </Button>
        {/if}
        {@html $config.strings.privacy_link}
    </footer>
</section>
{@html `<!--googleon: all-->`}

<style>
    :global(.e_consent *),
    :global(.e_consent *:before),
    :global(.e_consent *:after) {
        box-sizing: border-box;
    }

    .e_consent {
        all: unset;
        position: fixed;
        width: 100%;
        max-width: 1200px;
        background-color: white;
        left: 0;
        right: 0;
        margin-left: auto;
        margin-right: auto;
        padding-left: 9px;
        padding-right: 9px;
        box-shadow: 0 3px 15px 0 rgba(0, 0, 0, 0.33);
        font-size: 12px;
        line-height: 1.15;
        z-index: 9001;
        overflow: auto;
        max-height: 80vh;
        font-family: Arial, sans-serif;
        box-sizing: border-box;
    }

    .e_consent h2 {
        all: unset;
        display: block;
        font-weight: bold;
        font-size: 16px;
        line-height: 1.15;
        margin-bottom: 0;
    }

    .e_consent header {
        padding-top: 11px;
        padding-bottom: 4px;
        display: block;
        position: sticky;
        top: 0;
        background-color: white;
        z-index: 1;
    }

    .e_consent main {
        margin-top: 6px;
        display: block;
    }

    .e_consent footer {
        display: block;
        position: sticky;
        bottom: 0;
        background-color: white;
        padding-top: 4px;
        padding-bottom: 4px;
        z-index: 1;
    }

    .e_consent details {
        padding-left: 1rem;
    }

    .e_consent summary {
        color: #0f6dff;
        cursor: pointer;
        margin-left: -1rem;
    }

    :global(.e_consent p) {
        margin-top: 0.75em;
        margin-bottom: 0.75em;
    }

    @media (max-width: 580px) {
        :global(.e_consent p) {
            margin-top: 0.25em;
        }
    }

    /* THEME: bottom */
    .e_consent.e_consent--theme-bottom {
        bottom: 0;
    }

    :global(.e_consent.e_consent--theme-bottom a) {
        color: #0f6dff;
    }

    .e_consent--theme-bottom.e_consent--view-basic header {
        padding-bottom: 0;
    }

    :global(.e_consent.e_consent--theme-overlay p) {
        font-size: 11px;
        line-height: 1.5;
    }

    :global(.e_consent.e_consent--theme-bottom.e_consent--view-basic .e_consent__btn--advanced),
    :global(.e_consent.e_consent--theme-bottom.e_consent--view-basic .e_consent__btn--link),
    :global(.e_consent.e_consent--theme-bottom.e_consent--view-basic .e_consent__btn--save),
    :global(.e_consent.e_consent--theme-bottom.e_consent--view-basic footer > a),
    :global(.e_consent.e_consent--theme-bottom.e_consent--view-basic main > fieldset) {
        display: none !important;
    }

    :global(.e_consent.e_consent--theme-bottom.e_consent--view-advanced .e_consent__btn--accept),
    :global(.e_consent.e_consent--theme-bottom.e_consent--view-detailed .e_consent__btn--accept) {
        display: none !important;
    }

    .e_consent--theme-bottom.e_consent--view-basic {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
    }

    .e_consent--theme-bottom.e_consent--view-basic header {
        flex-basis: 100%;
    }

    .e_consent--theme-bottom.e_consent--view-basic main {
        flex-basis: 50%;
        flex-grow: 1;
    }

    /* THEME: overlay */
    .e_consent.e_consent--theme-overlay {
        max-width: 800px;
        top: calc(50% - 10vh);
        padding-left: 30px;
        padding-right: 30px;
        color: #000036;
        font-size: 14px;
        transform: translateY(-50%) !important;
        max-height: calc(100% - 30px - 20vh) !important;
    }

    .e_consent.e_consent--theme-overlay p {
        font-size: 11px;
        line-height: 1.5;
    }

    .e_consent.e_consent--theme-overlay summary {
        font-size: 14px;
        line-height: normal;
    }

    .e_consent__overlay.e_consent--theme-overlay {
        position: fixed;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        right: 0;
        background-color: rgba(0, 0, 0, 0.66);
        pointer-events: none;
        z-index: 9000;
    }

    @media (max-width: 580px) {
        .e_consent.e_consent--theme-overlay {
            padding-left: 20px;
            padding-right: 20px;
        }
    }

    :global(.e_consent.e_consent--theme-overlay a) {
        color: #52627c;
        font-weight: 700;
    }

    @media (max-width: 580px) {
        .e_consent--theme-overlay h2 {
            font-size: 22px;
        }
    }

    .e_consent--theme-overlay header {
        padding-top: 30px;
    }

    @media (max-width: 580px) {
        .e_consent--theme-overlay header {
            padding-top: 20px;
        }
    }

    .e_consent--theme-overlay main {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
    }

    @media (max-width: 580px) {
        .e_consent--theme-overlay main {
            flex-direction: column;
        }
    }

    .e_consent--theme-overlay footer {
        display: flex;
        flex-direction: row;
        padding-bottom: 30px;
        flex-wrap: wrap;
    }

    @media (max-width: 580px) {
        .e_consent--theme-overlay footer {
            padding-bottom: 15px;
        }
    }

    :global(.e_consent.e_consent--theme-overlay footer > a),
    :global(.e_consent.e_consent--theme-overlay footer > .e_consent__btn--link) {
        float: none;
    }

    :global(.e_consent.e_consent--theme-overlay footer > a) {
        order: -2;
        margin-right: auto;
        flex-grow: 1;
        display: inline;
        font-size: 12px;
        line-height: normal;
        margin: 0;
        flex-basis: 100%;
        font-weight: normal;
    }

    :global(.e_consent.e_consent--theme-overlay footer > .e_consent__btn--link) {
        order: -1;
    }

    :global(.e_consent.e_consent--theme-overlay .e_consent__btn--accept) {
        margin-right: 0;
    }

    @media (max-width: 580px) {
        :global(.e_consent.e_consent--theme-overlay .e_consent__btn--accept) {
            width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
        }
    }

    @media (max-width: 580px) {
        :global(.e_consent.e_consent--theme-overlay .e_consent__btn--save) {
            width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
        }
    }

    :global(.e_consent.e_consent--theme-overlay footer > .e_consent__btn--accept) {
        padding: 12px 26px;
        font-size: 14px;
        font-weight: normal;
        border-radius: 4px;
        line-height: normal;
        margin: 0;
        flex-basis: 100%;
        text-transform: uppercase;
        background-color: #AD0067;
    }

    :global(.e_consent.e_consent--theme-overlay footer > .e_consent__btn--decline) {
        width: 100%;
        margin-bottom: 15px;
    }

    :global(.e_consent.e_consent--theme-overlay footer > .e_consent--view-basic fieldset) {
        display: none;
    }

    :global(#pflt__container #pflt__inner) {
        max-height: 20vh;
    }
</style>
